<template>
  <!-- eslint-disable -->
  <div class="caseStudy view">
    <Header></Header>
    <div class="caseStudy__container layoutGrid">
      <h1 class="caseStudy__title h1">RadPiper</h1>
      <p class="caseStudy__paragraph p">
        RadPiper, also known as the Pipe Crawling Activity Measurement System (PCAMS), is a robotic system designed for the United States Department of energy. The system is designed to aide the cleanup of nuclear facilities. The robot crawls through a pipe identifying areas which contain harmful amounts of radioactive sediments.
      </p>
      <p class="caseStudy__paragraph p">
        My work on RadPiper consisted of two primary projects, designing a concept for a user interface using the images from the robot, and designing a tracking and labeling system for the full system.
      </p>
      <img
        src="@/assets/radpiper/rp_01.png"
        alt="image of the cuberover UI"
        class="caseStudy__image"
      />
      <h2 class="caseStudy__subtitle h2">
        User Interface Prototyping
      </h2>
      <p class="caseStudy__paragraph p">
        During an early phase of RadPiper’s development I was asked to generate a concept for how to use the images captured by the robot’s stereo cameras to enhance data collected by the robots other sensors.
      </p>
      <p class="caseStudy__em p p__em">
        How can we turn qualitative images into a more quantitative metric for analysis of radioactive sediments?
      </p>
      <h3 class="caseStudy__smallheader h3">
        Research
      </h3>
      <p class="caseStudy__paragraph p">
        For this project my research consisted largely of interviews with the engineers developing the system. We focused on the constraints and capabilities of the system as well as the objective of its operations.
      </p>
      <p class="caseStudy__paragraph p">
        The robot has two stereo cameras which it uses to image the pipe ahead. These cameras are angled across the center of the robot to enable them to image a larger area of the pipe. Unfortunately this makes the images difficult to interpret and the discreet forward facing images do not easily align to the linear dataset operators consider elsewhere in the analysis.
      </p>
      <img
        src="@/assets/radpiper/rp_02.jpg"
        alt="image of the cuberover UI"
        class="caseStudy__image"
      />
      <p class="caseStudy__paragraph p">
        The goal of the RadPiper system is to create a document which describes which sections of a linear pipe must be removed and disposed of in specialized facilities. Understanding how to align the data from the radiation sensors to the visual information from the cameras became the central focus of my design.
      </p>
      <h3 class="caseStudy__smallheader h3">
        Concept
      </h3>
      <p class="caseStudy__paragraph p">
        In order to transform the discrete image data into a linear dataset I developed an original image transformation technique. The technique leverages the consistent pose of the camera to its environment to select a small strip of each image which is matched to the pace at which the robot takes images.
      </p>
      <img
        src="@/assets/radpiper/rp_03.jpg"
        alt="image of the cuberover UI"
        class="caseStudy__image"
      />
      <p class="caseStudy__paragraph p">
        When the system performs this transformation on each image it is able to stitch together a new image which looks like an unwrapped version of the inside of a pipe.
      </p>
      <img
        src="@/assets/radpiper/rp_04.jpg"
        alt="image of the cuberover UI"
        class="caseStudy__image"
      />
      <p class="caseStudy__paragraph p">
        When the same transformation is used on the infrared cameras included on the robot it becomes incredibly easy to identify radioactive locations within a pipe.
      </p>
      <img
        src="@/assets/radpiper/rp_05.jpg"
        alt="image of the cuberover UI"
        class="caseStudy__image"
      />
      <h3 class="caseStudy__smallheader h3">
        Prototypes
      </h3>
      <p class="caseStudy__paragraph p">
        In this low-fi wireframe you can see how this unique image transformation can be used to generate image data which aligns with the linear radiation measurement dataset. This makes it easy for operators to identify which parts of the pipe exceed safe radiation limits.
      </p>
      <img
        src="@/assets/radpiper/rp_06.jpg"
        alt="image of the cuberover UI"
        class="caseStudy__image"
      />
      <h2 class="caseStudy__subtitle h2">
        Tracking System Design
      </h2>
      <p class="caseStudy__paragraph p">
        Eventually the RadPiper was refined into a production ready system which required additional design support. The complexity of the radpiper system, which included two robots along with a variety of support and deployment equipment, presented a massive communication issue during handoff of the system. I was asked to design a system for labeling and tracking each component of the system.
      </p>
      <img
        src="@/assets/radpiper/rp_07.jpg"
        alt="image of the cuberover UI"
        class="caseStudy__image"
      />
      <h3 class="caseStudy__smallheader h3">
        Research
      </h3>
      <p class="caseStudy__paragraph p">
        The first step in designing this tracking system was to identify all components in the system and record critical information like appropriate label sizes.
      </p>
      <img
        src="@/assets/radpiper/rp_08.jpg"
        alt="speed made good analysis summary"
        class="caseStudy__image--left caseStudy__image"
      />
      <img
        src="@/assets/radpiper/rp_09.jpg"
        alt="speed made good move distance analysis"
        class="caseStudy__image--right caseStudy__image"
      />
      <p class="caseStudy__paragraph p">
        Eventually all the components of the system were compiled into a single document which recorded the names, dimensions, functions and quantity of each component of the system. By the end of the project we were tracking 300+ components ranging from sensors to customized scissor lifts.
      </p>
      <img
        src="@/assets/radpiper/rp_10.jpg"
        alt="image of the cuberover UI"
        class="caseStudy__image--thin"
      />
      <h3 class="caseStudy__smallheader h3">
        Tracking Label Design
      </h3>
      <p class="caseStudy__paragraph p">
        The tracking labels themselves were designed to be clear and easy to read and clearly mark the component as part of the PCAMS system. Each label also included several numbers which indicated its specialization for pipe size and the robot with which it was associated.
      </p>
      <img
        src="@/assets/radpiper/rp_11.jpg"
        alt="image of the cuberover UI"
        class="caseStudy__image"
      />
      <h3 class="caseStudy__smallheader h3">
        Safety Label Design
      </h3>
      <p class="caseStudy__paragraph p">
        In addition to tracking labels I also designed several unique safety warning labels for equipment designed or modified for inclusion in the RadPiper system. These labels were designed to bring the RadPiper system into compliance with the DOE guidelines for hazard indications.
      </p>
      <img
        src="@/assets/radpiper/rp_12.jpg"
        alt="image of the cuberover UI"
        class="caseStudy__image"
      />
      <h3 class="caseStudy__smallheader h3">
        Operations Label Design
      </h3>
      <p class="caseStudy__paragraph p">
        Several components of the system required additional labels to guide operators when using the system. These labels were designed in conjunction with the RadPiper operations manual to ensure other teams could operate the robots safely and effectively.
      </p>
      <img
        src="@/assets/radpiper/rp_13.jpg"
        alt="image of the cuberover UI"
        class="caseStudy__image--thin"
      />
    </div>
  </div>
</template>

<script>
import Header from "@/components/header.vue";
export default {
  components: {
    Header
  }
};
</script>
